class MFooter extends HTMLElement {
  constructor() {
    super();

    this.mobileAccordion = null;
    this.mobileStickyBar = null;
    this.mobileQuery = null;
    this.items = [];
    this.boundButtons = new Map();
    this.initialized = false;
    this.transitionDuration = 250;

    this.handleMediaChange = this.handleMediaChange.bind(this);
    this.handleMatchMobile = this.handleMatchMobile.bind(this);
    this.handleUnmatchMobile = this.handleUnmatchMobile.bind(this);
    this.syncStickyBarHeight = this.syncStickyBarHeight.bind(this);
  }

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.init();
  }

  disconnectedCallback() {
    this.unbindAccordionButtons();

    if (this.mobileQuery) {
      if (typeof this.mobileQuery.removeEventListener === "function") {
        this.mobileQuery.removeEventListener("change", this.handleMediaChange);
      } else if (typeof this.mobileQuery.removeListener === "function") {
        this.mobileQuery.removeListener(this.handleMediaChange);
      }
    }

    document.removeEventListener("matchMobile", this.handleMatchMobile);
    document.removeEventListener("unmatchMobile", this.handleUnmatchMobile);
    window.removeEventListener("resize", this.syncStickyBarHeight);

    this.initialized = false;
  }

  init() {
    this.mobileAccordion = this.querySelector(".m-footer--accordion");
    this.mobileStickyBar = document.querySelector(".m-mobile-sticky-bar");
    this.mobileQuery = window.matchMedia("screen and (max-width: 767px)");

    this.bindAccordionButtons();
    this.applyAccordionState();
    this.syncStickyBarHeight();

    if (typeof this.mobileQuery.addEventListener === "function") {
      this.mobileQuery.addEventListener("change", this.handleMediaChange);
    } else if (typeof this.mobileQuery.addListener === "function") {
      this.mobileQuery.addListener(this.handleMediaChange);
    }

    document.addEventListener("matchMobile", this.handleMatchMobile);
    document.addEventListener("unmatchMobile", this.handleUnmatchMobile);
    window.addEventListener("resize", this.syncStickyBarHeight);
  }

  bindAccordionButtons() {
    if (!this.mobileAccordion) return;

    this.items = Array.from(
      this.mobileAccordion.querySelectorAll(".m-accordion--item")
    ).filter((item) => item.querySelector(".m-accordion--item-button"));

    this.items.forEach((item) => {
      const button = item.querySelector(".m-accordion--item-button");
      if (!button || this.boundButtons.has(button)) return;

      const handler = (event) => this.handleAccordionClick(event, item);
      button.addEventListener("click", handler);
      this.boundButtons.set(button, handler);
    });
  }

  unbindAccordionButtons() {
    this.boundButtons.forEach((handler, button) => {
      button.removeEventListener("click", handler);
    });

    this.boundButtons.clear();
  }

  handleAccordionClick(event, item) {
    if (!this.isMobile()) return;

    event.preventDefault();

    if (item.classList.contains("open")) {
      this.closeItem(item);
    } else {
      this.openItem(item);
    }
  }

  handleMediaChange() {
    this.applyAccordionState();
    this.syncStickyBarHeight();
  }

  handleMatchMobile() {
    this.applyAccordionState();
    this.syncStickyBarHeight();
  }

  handleUnmatchMobile() {
    this.applyAccordionState();
    this.syncStickyBarHeight();
  }

  isMobile() {
    if (this.mobileQuery) return this.mobileQuery.matches;
    return window.innerWidth <= 767;
  }

  applyAccordionState() {
    const isMobile = this.isMobile();

    this.items.forEach((item) => {
      const button = item.querySelector(".m-accordion--item-button");
      const content = item.querySelector(".m-accordion--item-content");

      if (!button || !content) return;

      if (!isMobile) {
        this.resetDesktopItem(button, content);
        return;
      }

      content.style.overflow = "hidden";
      content.style.transition = `max-height ${this.transitionDuration}ms ease, opacity ${this.transitionDuration}ms ease`;
      content.removeAttribute("hidden");

      if (item.classList.contains("open")) {
        this.setExpandedAttributes(button, content);
        content.style.opacity = "1";
        content.style.maxHeight = "none";
      } else {
        this.setCollapsedAttributes(button, content);
        content.style.opacity = "0";
        content.style.maxHeight = "0px";
      }
    });
  }

  resetDesktopItem(button, content) {
    button.setAttribute("aria-expanded", "true");
    content.setAttribute("aria-hidden", "false");
    content.removeAttribute("hidden");

    content.style.maxHeight = "";
    content.style.opacity = "";
    content.style.overflow = "";
    content.style.transition = "";
  }

  openItem(item) {
    const button = item.querySelector(".m-accordion--item-button");
    const content = item.querySelector(".m-accordion--item-content");

    if (!button || !content) return;

    item.classList.add("open");
    this.setExpandedAttributes(button, content);

    content.removeAttribute("hidden");
    content.style.overflow = "hidden";
    content.style.opacity = "1";
    content.style.transition = `max-height ${this.transitionDuration}ms ease, opacity ${this.transitionDuration}ms ease`;

    if (content.style.maxHeight === "none") {
      content.style.maxHeight = `${content.scrollHeight}px`;
    }

    requestAnimationFrame(() => {
      content.style.maxHeight = `${content.scrollHeight}px`;
    });

    window.setTimeout(() => {
      if (item.classList.contains("open") && this.isMobile()) {
        content.style.maxHeight = "none";
      }
    }, this.transitionDuration + 30);
  }

  closeItem(item) {
    const button = item.querySelector(".m-accordion--item-button");
    const content = item.querySelector(".m-accordion--item-content");

    if (!button || !content) return;

    item.classList.remove("open");
    this.setCollapsedAttributes(button, content);

    content.style.overflow = "hidden";
    content.style.transition = `max-height ${this.transitionDuration}ms ease, opacity ${this.transitionDuration}ms ease`;
    content.style.maxHeight = `${content.scrollHeight}px`;

    requestAnimationFrame(() => {
      content.style.opacity = "0";
      content.style.maxHeight = "0px";
    });
  }

  setExpandedAttributes(button, content) {
    button.setAttribute("aria-expanded", "true");
    content.setAttribute("aria-hidden", "false");
  }

  setCollapsedAttributes(button, content) {
    button.setAttribute("aria-expanded", "false");
    content.setAttribute("aria-hidden", "true");
  }

  syncStickyBarHeight() {
    const mobileStickyBar = document.querySelector(".m-mobile-sticky-bar");
    const stickyHeight = mobileStickyBar ? mobileStickyBar.offsetHeight : 0;

    document.documentElement.style.setProperty(
      "--mobile-sticky-bar-height",
      `${stickyHeight}px`
    );
  }
}

if (!customElements.get("m-footer")) {
  customElements.define("m-footer", MFooter);
}